import type { ReactNode } from 'react';
import type { Role } from '@amrita/shared';
import { Navigate } from 'react-router-dom';
import { useMe } from '@/hooks/useMe';
import { isStandalone } from '@/lib/tg';
import { Card } from './ui/Card';

interface Props {
  allow: Role[];
  children: ReactNode;
  /** Куда редиректить если не подходит (по-умолчанию `/`). */
  fallback?: string;
}

/**
 * Клиентский role-guard. Блокирует рендеринг страниц до получения /api/me,
 * показывает 403-карточку при недостатке прав, редиректит banned/paused на home.
 */
export function RequireRole({ allow, children, fallback = '/' }: Props) {
  const { data: me, isLoading, error } = useMe();

  if (isLoading) {
    return <div className="p-6 text-tg-hint">Загрузка…</div>;
  }

  if (error || !me) {
    return (
      <div className="p-6">
        <Card className="text-center space-y-2">
          <div className="text-lg">🔐 Не удалось авторизоваться</div>
          <div className="text-sm text-tg-hint">
            {isStandalone() ? (
              <>Сессия истекла, войдите заново: <a className="underline" href="/login">страница входа</a>.</>
            ) : (
              <>Не удалось получить данные Telegram. Перезапустите приложение через бота: отправьте /start.</>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (me.role === 'banned' || me.role === 'paused') {
    return <Navigate to={fallback} replace />;
  }

  if (!allow.includes(me.role)) {
    return (
      <div className="p-6">
        <Card className="text-center space-y-2">
          <div className="text-lg">⛔ Доступ ограничен</div>
          <div className="text-sm text-tg-hint">
            Эта страница доступна только: {allow.join(', ')}. Твоя роль: {me.role}.
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
